/* Feb-2026 · Wordcount milestone card.
 *
 * Shows the user's lifetime word count, the next milestone in front of
 * them, and a Claim button when they've crossed a threshold. Hidden if
 * the admin has disabled `milestone_bonuses`.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';   // Feb-2026 · i18n Wave 5
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Loader2, Trophy, Check, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import ZernioSmartComposer from '@/components/dialogs/ZernioSmartComposer';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

async function fetchJson(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!r.ok) throw new Error(`Request failed (${r.status})`);
  return r.json();
}

export default function MilestonesCard() {
  const { t } = useTranslation();
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [busy, setBusy]     = useState(false);
  // Feb-2026 · Post-claim "Share this win" state.
  const [lastClaim, setLastClaim] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);

  const load = () => {
    fetchJson('/milestones/status')
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoad(false));
  };
  useEffect(load, []);

  if (loading) return null;
  if (!data || !data.enabled) return null;

  const nextTier   = data.next_tier;
  const eligible   = (data.tiers || []).filter(t => t.eligible);
  const claimed    = (data.tiers || []).filter(t => t.claimed);
  const progressPct = nextTier
    ? Math.min(100, Math.round((data.words / nextTier.words) * 100))
    : 100;

  const claim = async () => {
    setBusy(true);
    try {
      const r = await fetchJson('/milestones/claim', { method: 'POST', body: '{}' });
      if (r?.total_tokens > 0) {
        toast.success(t('milestones.claimSuccess', { amount: `${(r.total_tokens / 1_000_000).toFixed(1)}M` }));
        // Feb-2026 · Preserve claim details so the "Share this win"
        // button can prefill the Smart Composer with a milestone caption.
        setLastClaim(r);
        load();
      } else {
        toast.info(t('milestones.keepWriting'));
      }
    } catch (e) {
      toast.error(e?.message || t('milestones.claimFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="milestones-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[13px]">
          <Trophy className="h-4 w-4 text-amber-500" /> {t('milestones.title')}
        </CardTitle>
        <p className="text-[11.5px] text-muted-foreground">
          {t('milestones.subtitle')}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="mb-1 flex items-center justify-between text-[11.5px]">
            <span className="font-mono-stat text-primary" data-testid="milestones-words">
              {t('milestones.yourWords', { count: data.words })}
            </span>
            {nextTier && (
              <span className="text-muted-foreground">
                {t('milestones.nextMilestone', { count: nextTier.words.toLocaleString(), label: nextTier.label })}
              </span>
            )}
          </div>
          <Progress value={progressPct} className="h-2" data-testid="milestones-progress" />
        </div>

        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {data.tiers.map((t) => (
            <div
              key={t.words}
              className={`flex items-center justify-between rounded-sm border px-2 py-1.5 text-[11px] ${t.claimed ? 'border-emerald-500/40 bg-emerald-500/5' : t.eligible ? 'border-amber-500/40 bg-amber-500/5' : 'border-border'}`}
              data-testid={`milestones-tier-${t.words}`}
            >
              <div>
                <div className="font-semibold">{t.label}</div>
                <div className="text-[10px] text-muted-foreground">
                  {t.words.toLocaleString()} words · {(t.tokens / 1_000_000).toFixed(1)}M tokens
                </div>
              </div>
              {t.claimed && <Check className="h-3.5 w-3.5 text-emerald-500" />}
              {t.eligible && !t.claimed && <span className="text-[10px] uppercase tracking-wider text-amber-500">Ready</span>}
            </div>
          ))}
        </div>

        {eligible.length > 0 && (
          <Button size="sm" onClick={claim} disabled={busy} className="w-full" data-testid="milestones-claim-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trophy className="h-3.5 w-3.5" />}
            {' '}{t('milestones.claim')} {eligible.length}
          </Button>
        )}
        {eligible.length === 0 && claimed.length > 0 && (
          <p className="text-[10.5px] text-center text-muted-foreground" data-testid="milestones-caught-up">
            You're all caught up — keep writing to hit the next tier.
          </p>
        )}
        {/* Feb-2026 · Share this win — surfaces after a successful claim
         * so the celebration is a single click away from cross-post. */}
        {lastClaim && lastClaim.granted?.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setComposerOpen(true)}
            className="w-full gap-1.5"
            data-testid="milestones-share-win-btn"
          >
            <Share2 className="h-3.5 w-3.5" /> {t('milestones.shareWin')}
          </Button>
        )}
      </CardContent>
      <ZernioSmartComposer
        open={composerOpen}
        onOpenChange={setComposerOpen}
        title={
          lastClaim?.granted?.[0]?.label
            ? `Milestone: ${lastClaim.granted[0].label} · ${lastClaim.words?.toLocaleString?.() || ''} words`
            : 'Wordcount milestone'
        }
        synopsis={
          lastClaim
            ? `Just hit ${lastClaim.words?.toLocaleString?.() || ''} lifetime words on Crown & Rose — thank you to every reader who's been along for the ride. On to the next chapter.`
            : ''
        }
      />
    </Card>
  );
}
